import { ParamMap } from "@angular/router";
import { DateTime } from "luxon";
import {
  emptyWorksheetGridClientFilters,
  WORKSHEET_REFERRAL_NOTIFICATION_FILTER_KEYS,
} from "../models/admissions.model";
import { AdmissionsService, WorklistDashboardFilterTag } from "../services/admissions.service";
import {
  AppliedFiltersState,
  FilterService,
} from "../services/filter.service";
import {
  getNotificationDashboardDateRangeInLocal,
  parseNotificationDashboardRange,
  type NotificationDashboardRange,
} from "./notification-dashboard-date-range";
import {
  DASHBOARD_WORKLIST_QP_BOOKED,
  DASHBOARD_WORKLIST_QP_ENCOUNTER_STATUS,
  DASHBOARD_WORKLIST_QP_ESTABLISHED,
  DASHBOARD_WORKLIST_QP_FILTER_LABEL,
  DASHBOARD_WORKLIST_QP_LOCATION,
  DASHBOARD_WORKLIST_QP_LOCATION_LABEL,
  DASHBOARD_WORKLIST_QP_NO_SHOW,
  DASHBOARD_WORKLIST_QP_PROSPECT_REFERRALS,
  DASHBOARD_WORKLIST_QP_REFERRAL_SOURCE,
  DASHBOARD_WORKLIST_QP_REFERRAL_SOURCE_LABEL,
  DASHBOARD_WORKLIST_QP_RESOURCE,
  DASHBOARD_WORKLIST_QP_RESOURCE_LABEL,
  DASHBOARD_WORKLIST_QP_VISIT_TYPE,
} from "./dashboard-worklist-patient-ids.util";

/** Dashboard Notifications card → deep link query keys (Patients / Worklist). */
export const DASHBOARD_NOTIFICATION_QP_ACTIVITY = "activityFilter";
export const DASHBOARD_NOTIFICATION_QP_RANGE = "notificationRange";

function isDashboardActivityFilterKey(key: string): boolean {
  return (
    key === "new" ||
    key === "missed_call" ||
    key === "unread_messages" ||
    key === "reminders" ||
    key === "voicemail"
  );
}

function activityKeyToReferralNotificationFilterKey(activityKey: string): string | null {
  switch (activityKey) {
    case "new":
      return "has_new_lead";
    case "missed_call":
      return "has_missed";
    case "unread_messages":
      return "has_sms";
    case "voicemail":
      return "has_voicemail";
    case "reminders":
      return "has_reminder";
    default:
      return null;
  }
}

export function dashboardActivityFilterDisplayLabel(activityKey: string): string {
  switch (activityKey) {
    case "new":
      return "New Referrals";
    case "missed_call":
      return "Missed Calls";
    case "unread_messages":
      return "Unread Messages";
    case "voicemail":
      return "New Voicemail";
    case "reminders":
      return "Reminders";
    default:
      return activityKey;
  }
}

export function notificationDashboardRangeDisplayLabel(
  range: NotificationDashboardRange
): string {
  switch (range) {
    case "today":
      return "Today";
    case "this_week":
      return "This Week";
    case "last_week":
      return "Last Week";
    case 7:
      return "Last 7 Days";
    case 14:
      return "14 Days";
    case 30:
      return "30 Days";
    case "all":
    default:
      return "All";
  }
}

function patchEngageFiltersDateRangeFromDashboardQuery(
  qp: ParamMap,
  c: AppliedFiltersState,
  range: NotificationDashboardRange
): void {
  if (range === "all") {
    c.dateRange = null;
    return;
  }
  const qpStart = (qp.get("startDate") || "").trim();
  const qpEnd = (qp.get("endDate") || "").trim();
  if (qpStart && qpEnd) {
    const startDate = new Date(qpStart);
    const endDate = new Date(qpEnd);
    if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
      c.dateRange = { startDate, endDate };
      return;
    }
  }
  const { start, end } = getNotificationDashboardDateRangeInLocal(range);
  c.dateRange = {
    startDate: start.toJSDate(),
    endDate: end.toJSDate(),
  };
}

/** Resolves dashboard notification period bounds (query ISO or preset range). */
function resolveNotificationDashboardDateRangeBounds(
  qp: ParamMap,
  range: NotificationDashboardRange
): { start: DateTime; end: DateTime } {
  const qpStart = (qp.get("startDate") || "").trim();
  const qpEnd = (qp.get("endDate") || "").trim();
  if (qpStart && qpEnd) {
    const start = DateTime.fromISO(qpStart);
    const end = DateTime.fromISO(qpEnd);
    if (start.isValid && end.isValid) {
      return { start, end };
    }
  }
  return getNotificationDashboardDateRangeInLocal(range);
}

/** Worklist: Created Date → PHI directory `startDate` / `endDate` (Prospects “Referrals” deep link). */
function patchWorksheetCreatedDatesFromDashboardQuery(
  qp: ParamMap,
  admissions: AdmissionsService,
  range: NotificationDashboardRange
): void {
  admissions.worksheetActivityDateStartIso = null;
  admissions.worksheetActivityDateEndIso = null;
  if (range === "all") {
    admissions.worksheetCreatedOnIso = null;
    admissions.worksheetCreatedOnEndIso = null;
    return;
  }
  const { start, end } = resolveNotificationDashboardDateRangeBounds(qp, range);
  const lo = start <= end ? start : end;
  const hi = start <= end ? end : start;
  admissions.worksheetCreatedOnIso = lo.toFormat("yyyy-MM-dd");
  admissions.worksheetCreatedOnEndIso = hi.toFormat("yyyy-MM-dd");
}

/** Worklist: DOS Date → PHI directory `dos_start` / `dos_end`. */
function patchWorksheetDosDatesFromDashboardQuery(
  qp: ParamMap,
  admissions: AdmissionsService,
  range: NotificationDashboardRange
): void {
  if (range === "all") {
    admissions.worksheetUserCommittedDosRange = false;
    return;
  }
  const { start, end } = resolveNotificationDashboardDateRangeBounds(qp, range);
  const lo = start <= end ? start : end;
  const hi = start <= end ? end : start;
  admissions.selectedDate = {
    startDate: lo.startOf("day").toJSDate(),
    endDate: hi.endOf("day").toJSDate(),
  };
  admissions.worksheetUserCommittedDosRange = true;
}

/** Worklist: Activity Date filter (`activity_date_start` / `activity_date_end`), not Created Date. */
function patchWorksheetActivityDatesFromDashboardQuery(
  qp: ParamMap,
  admissions: AdmissionsService,
  range: NotificationDashboardRange
): void {
  admissions.worksheetCreatedOnIso = null;
  admissions.worksheetCreatedOnEndIso = null;
  if (range === "all") {
    admissions.worksheetActivityDateStartIso = null;
    admissions.worksheetActivityDateEndIso = null;
    return;
  }
  const { start, end } = resolveNotificationDashboardDateRangeBounds(qp, range);
  const lo = start <= end ? start : end;
  const hi = start <= end ? end : start;
  admissions.worksheetActivityDateStartIso = lo.toFormat("yyyy-MM-dd");
  admissions.worksheetActivityDateEndIso = hi.toFormat("yyyy-MM-dd");
}

/** Tag label: `MM/dd/yyyy - MM/dd/yyyy` (not “Today” / “This Week”). */
export function dashboardActivityDateRangeTagLabel(
  qp: ParamMap,
  range: NotificationDashboardRange
): string {
  const { start, end } = resolveNotificationDashboardDateRangeBounds(qp, range);
  const lo = start <= end ? start : end;
  const hi = start <= end ? end : start;
  const startLabel = lo.toFormat("MM/dd/yyyy");
  const endLabel = hi.toFormat("MM/dd/yyyy");
  return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
}

function patchEngageFiltersActivityFromDashboardQuery(
  activityKey: string,
  c: AppliedFiltersState
): void {
  const notifKey = activityKeyToReferralNotificationFilterKey(activityKey);
  c.notifyTypes = [];
  c.stages = [];
  c.hasNewLead = null;
  c.hasReminder = null;
  c.hasStarred = 0;
  c.hasNotifications = null;
  c.referralNotificationFilterKeys = notifKey ? [notifKey] : [];
}

function buildWorklistDashboardFilterTags(
  activityKey: string,
  range: NotificationDashboardRange | null,
  qp: ParamMap,
  customActivityLabel?: string
): WorklistDashboardFilterTag[] {
  const tags: WorklistDashboardFilterTag[] = [];
  if (isDashboardActivityFilterKey(activityKey)) {
    const label = (customActivityLabel ?? "").trim();
    tags.push({
      id: "activity",
      label: label || dashboardActivityFilterDisplayLabel(activityKey),
    });
  }
  if (range != null && range !== "all") {
    tags.push({
      id: "dateRange",
      label: dashboardActivityDateRangeTagLabel(qp, range),
    });
  }
  return tags;
}

function syncWorklistReferralNotificationFiltersOnAdmissions(
  admissions: AdmissionsService,
  keys: string[]
): void {
  const allowed = new Set<string>(WORKSHEET_REFERRAL_NOTIFICATION_FILTER_KEYS);
  const next = keys.filter((k) =>
    allowed.has(k as (typeof WORKSHEET_REFERRAL_NOTIFICATION_FILTER_KEYS)[number])
  );
  admissions.worksheetGridText = {
    ...emptyWorksheetGridClientFilters(),
    ...(admissions.worksheetGridText ?? emptyWorksheetGridClientFilters()),
    referralNotificationFilters: next,
  };
}

function resetDashboardWorklistMetricFilterState(admissions: AdmissionsService): void {
  admissions.worksheetGridText = emptyWorksheetGridClientFilters();
  admissions.worksheetCreatedOnIso = null;
  admissions.worksheetCreatedOnEndIso = null;
  admissions.worksheetActivityDateStartIso = null;
  admissions.worksheetActivityDateEndIso = null;
  admissions.worksheetUpdatedOnIso = null;
  admissions.worksheetUserCommittedDosRange = false;
  admissions.referralSourceFilter = null;
  admissions.referralSourceFilterList.forEach((x) => (x.isSelected = false));
  admissions.locationFilter.forEach((x) => (x.isSelected = false));
  admissions.visitTypeFilter.forEach((x) => (x.isSelected = false));
  admissions.encounerStatusList.forEach((x) => (x.isSelected = false));
  admissions.resourcesFilter.forEach((x) => (x.isSelected = false));
  admissions.physicianFilter.forEach((x) => (x.isSelected = false));
  admissions.worklistDashboardFilterTags = [];
}

function applyDashboardSourceAndLocationFilters(
  qp: ParamMap,
  admissions: AdmissionsService
): void {
  const source = (qp.get(DASHBOARD_WORKLIST_QP_REFERRAL_SOURCE) ?? "").trim();
  const sourceLabel = (qp.get(DASHBOARD_WORKLIST_QP_REFERRAL_SOURCE_LABEL) ?? "").trim();
  admissions.referralSourceFilter = source || null;
  admissions.referralSourceFilterList.forEach((x) => {
    x.isSelected = !!source && String(x.value ?? "").trim().toLowerCase() === source.toLowerCase();
  });
  if (
    source &&
    !admissions.referralSourceFilterList.some(
      (x) => String(x.value ?? "").trim().toLowerCase() === source.toLowerCase()
    )
  ) {
    admissions.referralSourceFilterList.push({
      value: source,
      displayName: sourceLabel || source,
      isSelected: true,
    });
  }

  const location = (qp.get(DASHBOARD_WORKLIST_QP_LOCATION) ?? "").trim();
  const locationLabel = (qp.get(DASHBOARD_WORKLIST_QP_LOCATION_LABEL) ?? "").trim();
  admissions.locationFilter.forEach((x) => {
    x.isSelected = !!location && String(x.value ?? "").trim().toLowerCase() === location.toLowerCase();
  });
  if (
    location &&
    !admissions.locationFilter.some(
      (x) => String(x.value ?? "").trim().toLowerCase() === location.toLowerCase()
    )
  ) {
    admissions.locationFilter.push({
      value: location,
      displayName: locationLabel || location,
      isSelected: true,
    });
  }

  const resource = (qp.get(DASHBOARD_WORKLIST_QP_RESOURCE) ?? "").trim();
  const resourceLabel = (qp.get(DASHBOARD_WORKLIST_QP_RESOURCE_LABEL) ?? "").trim();
  if (resource) {
    admissions.resourcesFilter = [
      { value: "All", displayName: "All", isSelected: false },
      {
        value: resource,
        displayName: resourceLabel || resource,
        isSelected: true,
      },
    ];
  } else {
    admissions.resourcesFilter = [];
  }
}

/**
 * Apply dashboard Source / Schedule Resource / Location query params onto Engage {@link FilterService}
 * (channels + DOS resource/location) so the advanced filters UI and directory query match.
 * When a QP is omitted, the matching filter is cleared so a prior dashboard → Engage visit cannot stick.
 */
function applyDashboardSourceResourceLocationToFilterService(
  qp: ParamMap,
  filterService: FilterService
): void {
  const source = (qp.get(DASHBOARD_WORKLIST_QP_REFERRAL_SOURCE) ?? "").trim();
  const sourceLabel = (qp.get(DASHBOARD_WORKLIST_QP_REFERRAL_SOURCE_LABEL) ?? "").trim();
  const resource = (qp.get(DASHBOARD_WORKLIST_QP_RESOURCE) ?? "").trim();
  const resourceLabel = (qp.get(DASHBOARD_WORKLIST_QP_RESOURCE_LABEL) ?? "").trim();
  const location = (qp.get(DASHBOARD_WORKLIST_QP_LOCATION) ?? "").trim();
  const locationLabel = (qp.get(DASHBOARD_WORKLIST_QP_LOCATION_LABEL) ?? "").trim();

  const c = filterService.getCurrentFilters();
  c.channels = source
    ? [{ name: sourceLabel || source, value: source }]
    : [];
  filterService.setAllFilters(c);

  const dos = filterService.getDosFilters();
  dos.resource = resource
    ? { name: resourceLabel || resource, value: resource }
    : null;
  dos.location = location
    ? { name: locationLabel || location, value: location }
    : null;
  filterService.setDosFilters(dos);
}

/**
 * Dashboard Notifications tile deep link: apply `notificationRange` + `activityFilter` via {@link FilterService}
 * (and optional worklist {@link AdmissionsService} state). Returns whether any filter was applied.
 */
export function applyDashboardNotificationFiltersFromQueryParams(
  qp: ParamMap,
  filterService: FilterService,
  admissions?: AdmissionsService
): boolean {
  const rawRange = qp.get(DASHBOARD_NOTIFICATION_QP_RANGE);
  const range = parseNotificationDashboardRange(rawRange);
  const rawActivity = (qp.get(DASHBOARD_NOTIFICATION_QP_ACTIVITY) || "").trim();
  const activityKey = rawActivity.toLowerCase();

  const applyRange = range != null;
  const applyActivity = isDashboardActivityFilterKey(activityKey);

  if (!applyRange && !applyActivity) {
    return false;
  }

  const c = filterService.getCurrentFilters();
  c.referralIds = [];
  c.stages = [];
  c.assignedUsers = [];
  c.channels = [];
  c.statuses = [];
  c.types = [];
  c.tags = [];
  c.notifyTypes = [];
  c.hasNotifications = null;
  c.hasReminder = null;
  c.hasStarred = 0;
  c.hasNewLead = null;
  c.referralNotificationFilterKeys = [];
  c.dateRange = null;

  if (admissions) {
    resetDashboardWorklistMetricFilterState(admissions);
  }

  if (applyRange) {
    if (admissions) {
      patchWorksheetActivityDatesFromDashboardQuery(qp, admissions, range!);
      c.dateRange = null;
    } else {
      patchEngageFiltersDateRangeFromDashboardQuery(qp, c, range!);
    }
  } else if (admissions) {
    admissions.worksheetCreatedOnIso = null;
    admissions.worksheetCreatedOnEndIso = null;
    admissions.worksheetActivityDateStartIso = null;
    admissions.worksheetActivityDateEndIso = null;
  }

  if (applyActivity) {
    patchEngageFiltersActivityFromDashboardQuery(activityKey, c);
    if (admissions) {
      syncWorklistReferralNotificationFiltersOnAdmissions(
        admissions,
        c.referralNotificationFilterKeys ?? []
      );
      const customLabel = (qp.get(DASHBOARD_WORKLIST_QP_FILTER_LABEL) ?? "").trim();
      admissions.worklistDashboardFilterTags = buildWorklistDashboardFilterTags(
        activityKey,
        applyRange ? range! : null,
        qp,
        customLabel || undefined
      );
      admissions.notifyWorklistDashboardFilterTagsChanged();
    }
  } else if (admissions) {
    admissions.worklistDashboardFilterTags = [];
    admissions.notifyWorklistDashboardFilterTagsChanged();
  }

  filterService.setWorklistEngagePatientIdFilter(null);
  filterService.setAllFilters(c);
  filterService.clearDosFilters();
  applyDashboardSourceResourceLocationToFilterService(qp, filterService);
  if (admissions) {
    applyDashboardSourceAndLocationFilters(qp, admissions);
  }
  filterService.applyFilters();
  filterService.markEngageQuickActivityFilterUsed();
  return true;
}

/**
 * Prospects dashboard “Referrals” → worklist: “Referral” tag + Created Date on directory GET
 * (`startDate` / `endDate`). No `has_new_lead` or `activity_date_*`.
 */
export function applyDashboardProspectReferralsFromQueryParams(
  qp: ParamMap,
  admissions: AdmissionsService,
  filterService: FilterService
): boolean {
  if ((qp.get(DASHBOARD_WORKLIST_QP_PROSPECT_REFERRALS) ?? "").trim() !== "1") {
    return false;
  }

  const rawRange = qp.get(DASHBOARD_NOTIFICATION_QP_RANGE);
  const range = parseNotificationDashboardRange(rawRange) ?? "this_week";

  resetDashboardWorklistMetricFilterState(admissions);

  const c = filterService.getCurrentFilters();
  c.referralNotificationFilterKeys = [];
  c.hasNewLead = null;
  c.hasNotifications = null;
  c.hasReminder = null;
  c.dateRange = null;
  c.channels = [];
  filterService.setWorklistEngagePatientIdFilter(null);
  filterService.setAllFilters(c);
  filterService.clearDosFilters();

  patchWorksheetCreatedDatesFromDashboardQuery(qp, admissions, range);
  applyDashboardSourceAndLocationFilters(qp, admissions);
  applyDashboardSourceResourceLocationToFilterService(qp, filterService);
  filterService.applyFilters();
  filterService.markEngageQuickActivityFilterUsed();

  const tags: WorklistDashboardFilterTag[] = [{ id: "prospectReferral", label: "Referral" }];
  if (range !== "all") {
    tags.push({
      id: "dateRange",
      label: dashboardActivityDateRangeTagLabel(qp, range),
    });
  }
  admissions.worklistDashboardFilterTags = tags;
  admissions.notifyWorklistDashboardFilterTagsChanged();
  return true;
}

/**
 * Prospects dashboard “Booked” → worklist: Created Date + Encounter Status = Scheduled,
 * plus dashboard Source when selected.
 */
export function applyDashboardBookedFromQueryParams(
  qp: ParamMap,
  admissions: AdmissionsService,
  filterService: FilterService
): boolean {
  if ((qp.get(DASHBOARD_WORKLIST_QP_BOOKED) ?? "").trim() !== "1") {
    return false;
  }

  const rawRange = qp.get(DASHBOARD_NOTIFICATION_QP_RANGE);
  const range = parseNotificationDashboardRange(rawRange) ?? "this_week";

  resetDashboardWorklistMetricFilterState(admissions);

  const c = filterService.getCurrentFilters();
  c.referralNotificationFilterKeys = [];
  c.hasNewLead = null;
  c.hasNotifications = null;
  c.hasReminder = null;
  c.dateRange = null;
  c.channels = [];
  filterService.setWorklistEngagePatientIdFilter(null);
  filterService.setAllFilters(c);
  filterService.applyFilters();

  patchWorksheetCreatedDatesFromDashboardQuery(qp, admissions, range);

  admissions.encounerStatusList.forEach((x) => {
    x.isSelected = String(x.value ?? "").trim().toLowerCase() === "scheduled";
  });

  applyDashboardSourceAndLocationFilters(qp, admissions);

  admissions.worklistDashboardFilterTags = [];
  admissions.notifyWorklistDashboardFilterTagsChanged();
  return true;
}

/** Encounters dashboard Open / Sign Req / Unsubmitted → worklist: DOS Date + encounter_status. */
export function applyDashboardEncounterStatusFromQueryParams(
  qp: ParamMap,
  admissions: AdmissionsService,
  filterService: FilterService
): boolean {
  const encounterStatus = (qp.get(DASHBOARD_WORKLIST_QP_ENCOUNTER_STATUS) ?? "").trim();
  if (!encounterStatus) {
    return false;
  }

  const rawRange = qp.get(DASHBOARD_NOTIFICATION_QP_RANGE);
  const range = parseNotificationDashboardRange(rawRange) ?? "this_week";

  resetDashboardWorklistMetricFilterState(admissions);

  const c = filterService.getCurrentFilters();
  c.referralNotificationFilterKeys = [];
  c.hasNewLead = null;
  c.hasNotifications = null;
  c.hasReminder = null;
  c.dateRange = null;
  c.channels = [];
  filterService.setWorklistEngagePatientIdFilter(null);
  filterService.setAllFilters(c);
  filterService.applyFilters();

  patchWorksheetDosDatesFromDashboardQuery(qp, admissions, range);

  admissions.encounerStatusList.forEach((x) => {
    x.isSelected =
      String(x.value ?? "").trim().toLowerCase() === encounterStatus.toLowerCase();
  });
  applyDashboardSourceAndLocationFilters(qp, admissions);

  admissions.worklistDashboardFilterTags = [];
  admissions.notifyWorklistDashboardFilterTagsChanged();
  return true;
}

/** Upcoming Visits dashboard Clinic / Telehealth / Procedures / Other → worklist: DOS Date + visit_type. */
export function applyDashboardVisitTypeFromQueryParams(
  qp: ParamMap,
  admissions: AdmissionsService,
  filterService: FilterService
): boolean {
  const visitType = (qp.get(DASHBOARD_WORKLIST_QP_VISIT_TYPE) ?? "").trim();
  if (!visitType) {
    return false;
  }

  const rawRange = qp.get(DASHBOARD_NOTIFICATION_QP_RANGE);
  const range = parseNotificationDashboardRange(rawRange) ?? "today";

  resetDashboardWorklistMetricFilterState(admissions);

  const c = filterService.getCurrentFilters();
  c.referralNotificationFilterKeys = [];
  c.hasNewLead = null;
  c.hasNotifications = null;
  c.hasReminder = null;
  c.dateRange = null;
  c.channels = [];
  filterService.setWorklistEngagePatientIdFilter(null);
  filterService.setAllFilters(c);
  filterService.applyFilters();

  patchWorksheetDosDatesFromDashboardQuery(qp, admissions, range);

  admissions.visitTypeFilter.forEach((x) => {
    x.isSelected =
      String(x.value ?? "").trim().toLowerCase() === visitType.toLowerCase();
  });
  if (
    visitType &&
    !admissions.visitTypeFilter.some(
      (x) => String(x.value ?? "").trim().toLowerCase() === visitType.toLowerCase()
    )
  ) {
    admissions.visitTypeFilter.push({
      value: visitType,
      displayName: visitType,
      isSelected: true,
    });
  }
  applyDashboardSourceAndLocationFilters(qp, admissions);

  admissions.worklistDashboardFilterTags = [];
  admissions.notifyWorklistDashboardFilterTagsChanged();
  return true;
}

/** Prospects dashboard “No Show” → worklist: DOS Date + Encounter Status = No Show. */
export function applyDashboardNoShowFromQueryParams(
  qp: ParamMap,
  admissions: AdmissionsService,
  filterService: FilterService
): boolean {
  if ((qp.get(DASHBOARD_WORKLIST_QP_NO_SHOW) ?? "").trim() !== "1") {
    return false;
  }

  const rawRange = qp.get(DASHBOARD_NOTIFICATION_QP_RANGE);
  const range = parseNotificationDashboardRange(rawRange) ?? "this_week";

  resetDashboardWorklistMetricFilterState(admissions);

  const c = filterService.getCurrentFilters();
  c.referralNotificationFilterKeys = [];
  c.hasNewLead = null;
  c.hasNotifications = null;
  c.hasReminder = null;
  c.dateRange = null;
  c.channels = [];
  filterService.setWorklistEngagePatientIdFilter(null);
  filterService.setAllFilters(c);
  filterService.applyFilters();

  patchWorksheetDosDatesFromDashboardQuery(qp, admissions, range);

  admissions.encounerStatusList.forEach((x) => {
    x.isSelected = String(x.value ?? "").trim().toLowerCase() === "no show";
  });
  applyDashboardSourceAndLocationFilters(qp, admissions);

  admissions.worklistDashboardFilterTags = [];
  admissions.notifyWorklistDashboardFilterTagsChanged();
  return true;
}

/** Prospects dashboard “Established” → worklist: DOS Date + `lead_status=established`. */
export function applyDashboardEstablishedFromQueryParams(
  qp: ParamMap,
  admissions: AdmissionsService,
  filterService: FilterService
): boolean {
  if ((qp.get(DASHBOARD_WORKLIST_QP_ESTABLISHED) ?? "").trim() !== "1") {
    return false;
  }

  const rawRange = qp.get(DASHBOARD_NOTIFICATION_QP_RANGE);
  const range = parseNotificationDashboardRange(rawRange) ?? "this_week";

  resetDashboardWorklistMetricFilterState(admissions);

  const c = filterService.getCurrentFilters();
  c.referralNotificationFilterKeys = [];
  c.hasNewLead = null;
  c.hasNotifications = null;
  c.hasReminder = null;
  c.dateRange = null;
  c.channels = [];
  filterService.setWorklistEngagePatientIdFilter(null);
  filterService.setAllFilters(c);
  filterService.applyFilters();

  admissions.worksheetGridText = {
    ...emptyWorksheetGridClientFilters(),
    refStatusContains: "established",
  };
  patchWorksheetDosDatesFromDashboardQuery(qp, admissions, range);
  applyDashboardSourceAndLocationFilters(qp, admissions);

  admissions.worklistDashboardFilterTags = [];
  admissions.notifyWorklistDashboardFilterTagsChanged();
  return true;
}

/**
 * Clears notification deep-link filter state (FilterService + worklist grid dates/merge).
 * Does not remove patient-scope tags or the worklist `patientId` filter.
 */
export function resetDashboardNotificationFilterState(
  filterService: FilterService,
  admissions?: AdmissionsService
): void {
  const c = filterService.getCurrentFilters();
  c.notifyTypes = [];
  c.hasNewLead = null;
  c.hasReminder = null;
  c.hasStarred = 0;
  c.hasNotifications = null;
  c.referralNotificationFilterKeys = [];
  c.dateRange = null;
  filterService.setAllFilters(c);
  filterService.applyFilters();
  filterService.markEngageQuickActivityFilterUsed();
  if (admissions) {
    const gt = {
      ...emptyWorksheetGridClientFilters(),
      ...(admissions.worksheetGridText ?? emptyWorksheetGridClientFilters()),
    };
    gt.referralNotificationFilters = [];
    admissions.worksheetGridText = gt;
    admissions.worksheetCreatedOnIso = null;
    admissions.worksheetCreatedOnEndIso = null;
    admissions.worksheetActivityDateStartIso = null;
    admissions.worksheetActivityDateEndIso = null;
    admissions.worklistMergeEngageFromReferralsApi = false;
  }
}

/** Clears quick activity + referral date range used by dashboard deep links (Engage + Worklist). */
export function clearDashboardNotificationEngageFilters(
  filterService: FilterService,
  admissions?: AdmissionsService
): void {
  resetDashboardNotificationFilterState(filterService, admissions);
  if (admissions) {
    admissions.clearWorklistDashboardDeepLinkFilters();
  }
}
